import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import styled, { css, useTheme } from 'styled-components';
import { IRecommandCollaborationDB, IRecommandCollaborationListDB } from 'type/wconcepdb';

import TableCell from 'components/common/table/cell/TableCell';
import {
	useGetCreatorCollaborationList,
	useGetRecommendationList,
} from 'hooks/queries/creator';
import useCollabolationTable from 'hooks/table/useCollabolationTable';
import useGetSearchParams from 'hooks/useGetSearchParams';
import CoreSelect from 'shared/CoreSelect';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import style from 'styles/styled-components/style';
import { collaboration_header_arr } from 'utils/table';

import SearchContentBoard from './SearchContentBoard';

const SearchResult = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const [recommendationId, platform, sort, matchLevel, keyword] = useGetSearchParams([
		'recommendationId',
		'platform',
		'sort',
		'matchLevel',
		'keyword',
	]);
	const theme = useTheme();
	const [activePk, setActivePk] = useState<number | null>(null);

	const match_cell_mapper = useCollabolationTable(
		platform as 'ig' | 'yt',
		recommendationId,
	);

	//추천 그룹 리스트
	const { data: recommandGroupList, isLoading: recommandGroupListLoading } =
		useGetRecommendationList();

	const paramPlatform = platform === 'ig' ? 'instagram' : 'youtube';
	const { data: collaborationList, isLoading: collaborationListLoading } =
		useGetCreatorCollaborationList(recommendationId as string, paramPlatform);

	const groups = recommandGroupList;
	const month = groups?.find(
		(group) => group.recommendationId === parseInt(recommendationId || ''),
	)?.recommendationMonth;
	const formattedMonth = month
		? `${new Date(month).getFullYear()}년 ${new Date(month).getMonth() + 1}월`
		: '';

	const matchLavelOptions: {
		label: string;
		value: 'all' | 'mega' | 'macro' | 'micro';
	}[] = [
		{ label: '매칭레벨', value: 'all' },
		{ label: 'S등급/메가', value: 'mega' },
		{ label: 'A등급/매크로', value: 'macro' },
		{ label: 'B등급/마이크로', value: 'micro' },
	];

	const selectedMatchLevel =
		matchLavelOptions.find((option) => option.value === matchLevel) ||
		matchLavelOptions[0];

	const dataSource = collaborationList;

	let selectedListDatasource: IRecommandCollaborationDB[] = [];
	if (matchLevel === 'all') {
		selectedListDatasource = [
			...(dataSource?.macro || []),
			...(dataSource?.micro || []),
			...(dataSource?.mega || []),
		];
	} else {
		selectedListDatasource =
			dataSource?.[matchLevel as keyof IRecommandCollaborationListDB] ?? [];
	}

	// 키워드로 필터링된 크리에이터 리스트
	// const filteredCreatorList = keyword
	// 	? selectedListDatasource.filter(
	// 			(creator) =>
	// 				creator.username.toLowerCase().includes(keyword.toLowerCase()) ||
	// 				creator.biography.toLowerCase().includes(keyword.toLowerCase()),
	// 	  )
	// 	: selectedListDatasource;
	const filteredCreatorList = selectedListDatasource;

	const handleMathcLevelChange = (selectedOption: string) => {
		const selectedMatchLevel = matchLavelOptions.find(
			(option) => option.label === selectedOption,
		);
		searchParams.set('matchLevel', selectedMatchLevel?.value ?? '');
		setSearchParams(searchParams);
	};

	const handleRowClick = (row_data: IRecommandCollaborationDB) => {
		const creatorPk = row_data.creatorPk;
		navigate(`/creator/report/${platform}/${recommendationId}/${creatorPk}`);
	};

	// if (recommandGroupListLoading || recommandationLoading) {
	// 	return (
	// 		<List>
	// 			<Grid _height="calc(100vh - 400px)" flex="center">
	// 				<CoreLoading color_type="black" />
	// 			</Grid>
	// 		</List>
	// 	);
	// }

	useEffect(() => {
		if (!matchLevel) {
			searchParams.set('matchLevel', 'all');
			setSearchParams(searchParams, { replace: true });
		}
	}, [matchLevel, searchParams]);

	return (
		<List>
			<Grid flex="between" _margin="8px 0 4px 0">
				<StateText>
					{`${formattedMonth} 크리에이터 계정이
					${filteredCreatorList?.length ?? 0} 건 발견 되었습니다.`}
				</StateText>
				<CoreSelect
					select_type={'box'}
					_height="35px"
					_width="170px"
					option_list={matchLavelOptions.map((item) => item.label)}
					selected_option={selectedMatchLevel.label}
					setSelectedOption={handleMathcLevelChange}
				/>
			</Grid>

			<TableHeaderWrapper>
				{collaboration_header_arr.map((item, idx) => (
					<React.Fragment key={idx}>
						<TableCell cell_type="common" _width={item.width}>
							<Grid _width={`${item.width}px`} flex="center" align="center">
								<Grid flex="center" _gap="4px">
									<Text white_space="pre" typo="span">
										{item.header}
									</Text>
								</Grid>

								{/* 구분 선 */}
							</Grid>
						</TableCell>
						{idx !== collaboration_header_arr?.length - 1 && (
							<Contour _height="21px" _width="1px" />
						)}
					</React.Fragment>
				))}
			</TableHeaderWrapper>
			<TableResultWrapper>
				{!collaborationListLoading && filteredCreatorList.length === 0 && (
					<TableRow selected={false}>
						<Grid flex="center">
							<Text typo="span" color="gray">
								{'데이터가 없습니다.'}
							</Text>
						</Grid>
					</TableRow>
				)}
				{filteredCreatorList?.map((data, row_idx: number) => (
					<React.Fragment key={row_idx}>
						<TableRow
							onClick={() => {
								handleRowClick(data);
							}}
							selected={false}
						>
							{collaboration_header_arr.map((item, idx) => (
								<React.Fragment key={idx}>
									{match_cell_mapper[item.name](data, idx, row_idx)}

									{item.name === 'contents' && (
										<TableCell _width={collaboration_header_arr[idx].width}>
											<Grid
												flex="center"
												_width="100%"
												_height="100%"
												onClick={(e) => {
													e.stopPropagation();
													if (activePk === data.creatorPk) {
														setActivePk(null);
													} else setActivePk(data.creatorPk);
												}}
											>
												{activePk === data.creatorPk ? (
													<CoreSvg>
														<Icon.DropArrowUp />
													</CoreSvg>
												) : (
													<CoreSvg>
														<Icon.DropArrowDown />
													</CoreSvg>
												)}
											</Grid>
										</TableCell>
									)}
								</React.Fragment>
							))}
						</TableRow>
						{activePk === data.creatorPk && (
							<SearchContentBoard
								values={data.collaboratedContents}
								platform={platform as 'ig' | 'yt'}
								recommendationId={recommendationId}
								creatorPk={data.creatorPk}
							/>
						)}
					</React.Fragment>
				))}
			</TableResultWrapper>
		</List>
	);
};
const { Grid, Text } = style;

const TableHeaderWrapper = styled(Grid)`
	height: 66px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	padding: 0 16px;

	align-items: center;
	gap: 0;

	${({ theme }) => theme.common.flexStart};

	div {
		cursor: pointer;
	}
`;

const TableResultWrapper = styled(Grid)`
	margin-bottom: 8px;
`;

const Contour = styled(Grid)`
	background-color: ${({ theme }) => theme.colors.gray.gray15};
	min-width: 1px;
`;
const TableRow = styled(Grid)<{ selected: boolean }>`
	min-width: calc(100% - 32px);
	height: 65px;

	border-radius: 8px;
	margin-top: 8px;
	padding: 0 16px;

	${({ theme }) => theme.common.flexStart}

	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray_white};

	${({ theme, selected }) =>
		selected &&
		css`
			border: 1px solid ${theme.colors.gray.gray_black};
		`}

	&:hover {
		cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
		border-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		${({ theme, selected }) =>
			selected &&
			css`
				border-color: ${theme.colors.uplus.primary50_key};
			`}
	}
`;
const List = styled.div`
	width: 100%;
	height: max-content;
	max-width: 1400px;

	${({ theme }) => theme.common.flexStart};
	flex-direction: column;

	gap: 4px;
`;

const StateText = styled(Text)`
	${({ theme }) => theme.typo.body.span};
`;

export default SearchResult;
